import React, { useState, useEffect } from "react"
import tw, { css } from "twin.macro"

// Utils
import { usePrevious } from "../utils/hooks/usePrevious"
import { SlideProps } from "../slides"

// Components
import Speedometer from "./speedometer"
import CourseDurationHighlight from "../svgs/courseDurationHighlight"

interface Props {
  slideVisible: boolean
}

const TimeSelector: React.FC<Props> = ({slideVisible}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const previousProps = usePrevious({ slideVisible })
  useEffect(() => {
    // When the slide appears and disappears, make the needle move
    if (previousProps?.slideVisible !== slideVisible) {
      setSelectedIndex(slideVisible ? 3 : 0)
    }  
  }, [slideVisible])

  const durationButtonWidth = 9
  const durationScaleAmount = 0.44
  // Small
  const durationButtonMargins = -2.7
  const selectedDurationButtonMargins = -0.3
  // Medium+
  const mdDurationButtonMargins = -1.5
  const mdSelectedDurationButtonMargins = 0.7

  return (
      <div css={[css`max-width: 100vw`, tw`flex flex-col items-center select-none overflow-hidden md:overflow-visible`]}>
        <div
          css={[
            css`
              width: 19rem;
              height: 14.7em;
              @media (min-width: 768px) {
                width: 26rem;
                height: 20rem;
              }
            `,
            tw`mb-4`,
          ]}
        >
          <Speedometer
            positions={timeValues.map(({ week }) => week)}
            duration={timeValues[selectedIndex].week}
            selectedIndex={selectedIndex}
            onClick={setSelectedIndex}
          />
        </div>

        {/* The graphic that shows the selected total duration */}
        <div css={[
          css`
            width: 7.5rem;
            @media (min-width: 768px) {
              width: 10rem;
            };
          `
        ]}>
          <CourseDurationHighlight />
        </div>

        {/* Total duration buttons */}
        <div
          css={[
            css`
              transform: translate(
                -${selectedIndex * (durationButtonWidth + durationButtonMargins*2)}rem,
                0
              );
              @media (min-width: 768px) {
                transform: translate(
                  -${selectedIndex * (durationButtonWidth + mdDurationButtonMargins*2)}rem,
                  0
                );
              }
            `,
            css`
              margin-top: -4.7rem;
              margin-left: ${(durationButtonWidth+durationButtonMargins*2) * 4}rem;
              @media (min-width: 768px) {
                margin-top: -5.7rem;
                margin-left: ${(durationButtonWidth+mdDurationButtonMargins*2) * 4}rem;
              }
              transition-duration: 300ms;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-property: transform;
            `,
            tw`flex flex-row text-xl overflow-visible`,
          ]}
        >
          {timeValues.map((value, idx) => (
            <button
              css={[
                css`
                  width: ${durationButtonWidth}rem;
                  margin: 0 ${idx == selectedIndex ? selectedDurationButtonMargins : durationButtonMargins}rem;
                  @media(min-width: 768px) {
                    margin: 0 ${idx == selectedIndex ? mdSelectedDurationButtonMargins : mdDurationButtonMargins}rem;
                  }
                  transform: scale(${idx == selectedIndex ? 1.0 : durationScaleAmount});
                `,
                css`
                  transition-duration: 300ms;
                  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                  transition-property: transform, margin;
                `,
                tw`text-2xl md:text-4xl font-licenz py-8 focus:outline-none text-brand-yellow opacity-50 hover:opacity-100 uppercase`,
                idx == selectedIndex &&
                  tw`text-brand-yellow hover:text-brand-yellow opacity-100`,
              ]}
              key={idx}
              onClick={() => {
                setSelectedIndex(idx)
              }}
            >
              {value.total} {value.total == 1 ? "month" : "months"}
            </button>
          ))}
        </div>
      </div>
  )
}

interface TimeValue {
  week: number
  total: number
}

const timeValues: TimeValue[] = [
  { week: 10, total: 5 },
  { week: 13, total: 4 },
  { week: 17, total: 3 },
  { week: 25, total: 2 },
  { week: 50, total: 1 },
]

export default TimeSelector
